import { isBrazilianState } from '@crm/locale';
import { z } from 'zod';
import type { AddressRecord } from '@web/core/address/address-form-fields';
import { DOCUMENT_CHARACTERS, isValidCnpj, isValidCpf } from '@crm/documents';
import { customFieldValuesSchema, isIsoDate, normalizeLabel } from '../custom-fields/contract';
import type { CustomFieldValues } from '../custom-fields/contract';
import type { NextTask } from '../tasks/contract';

/**
 * The customers and contacts contract (docs/specs/004-clientes-e-contatos/spec.md, §3 and
 * §6): the shapes the API answers and the zod schemas of what it accepts. The forms and the
 * mocked API (`src/mocks/customers`) validate with these same schemas, so the screen and
 * the future backend agree on every rule written here.
 */

/* ------------------------------------------------------------------ constants */

export const CUSTOMER_TYPES = ['individual', 'company'] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];

export const CUSTOMER_SITUATIONS = ['lead', 'active', 'inactive'] as const;
export type CustomerSituation = (typeof CUSTOMER_SITUATIONS)[number];

export const ORIGIN_CHANNELS = [
  'whatsapp',
  'instagram',
  'facebook',
  'email',
  'website',
  'referral',
  'event',
  'other',
] as const;
export type OriginChannel = (typeof ORIGIN_CHANNELS)[number];

export const PHONE_LABELS = ['mobile', 'work', 'whatsapp'] as const;
export type PhoneLabel = (typeof PHONE_LABELS)[number];

export const CUSTOMER_SORTS = [
  'name',
  '-name',
  'createdAt',
  '-createdAt',
  'situation',
  '-situation',
] as const;
export type CustomerSort = (typeof CUSTOMER_SORTS)[number];
export const DEFAULT_CUSTOMER_SORT: CustomerSort = '-createdAt';

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

/** `ownerMembershipId=none` lists the customers without an owner. */
export const NO_OWNER = 'none';

/** Prefix of the custom field filters of the list: `cf.<key>=<value>`. */
export const CUSTOM_FIELD_FILTER_PREFIX = 'cf.';

export const CUSTOMER_LIMITS = {
  nameMin: 2,
  nameMax: 160,
  originDetailMax: 160,
  noteMax: 500,
  tagMax: 40,
  tagsMax: 20,
  jobTitleMax: 80,
  phonesMax: 5,
  emailsMax: 5,
  searchMax: 120,
} as const;

/** Codes of the errors of this contract (translated in `errors.api.<CODE>`). */
export const CUSTOMER_ERROR_CODES = [
  'CUSTOMER_INVALID',
  'CONTACT_INVALID',
  'CUSTOM_FIELD_INVALID',
  'CUSTOM_FIELD_VALUE_INVALID',
  'CUSTOMER_OWNER_INVALID',
  'CUSTOMER_UNIT_INVALID',
  'CUSTOMER_OWNER_OUT_OF_REACH',
  'CUSTOMER_UNIT_OUT_OF_REACH',
  'CUSTOMER_NOT_FOUND',
  'CONTACT_NOT_FOUND',
  'CUSTOM_FIELD_NOT_FOUND',
  'CUSTOMER_DOCUMENT_TAKEN',
  'CUSTOMER_ALREADY_OWNED',
  'CUSTOMER_ARCHIVED',
  'CUSTOMER_AVATAR_INVALID',
  'CONTACT_PRIMARY_REQUIRED',
  'CUSTOM_FIELD_LABEL_TAKEN',
  'CUSTOM_FIELD_LOCKED',
] as const;
export type CustomerErrorCode = (typeof CUSTOMER_ERROR_CODES)[number];

/* ------------------------------------------------------------------ shapes */

export type Address = AddressRecord;

export interface CustomerTag {
  id: string;
  name: string;
}

export interface CustomerTagWithUsage extends CustomerTag {
  usageCount: number;
}

export interface PersonRef {
  membershipId: string;
  name: string;
}

export interface UnitRef {
  id: string;
  name: string;
}

export interface ContactSummary {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
}

export interface CustomerSummary {
  id: string;
  type: CustomerType;
  displayName: string;
  /** PF: the person's name; PJ: the legal name (razão social). */
  name: string;
  /** PJ only. */
  tradeName: string | null;
  /**
   * Without punctuation: a CPF is 11 digits; a CNPJ 14 characters, the first 12 digits or
   * upper-case letters (the alphanumeric CNPJ) and the last 2 digits.
   */
  document: string | null;
  /** The uploaded picture; null shows the initials. */
  avatarUrl: string | null;
  situation: CustomerSituation;
  owner: PersonRef | null;
  unit: UnitRef;
  tags: CustomerTag[];
  primaryContact: ContactSummary | null;
  /** The nearest open task of the customer or its deals (docs/specs/006, rule 17). */
  nextTask: NextTask | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerOrigin {
  channel: OriginChannel | null;
  detail: string | null;
}

export interface Customer extends CustomerSummary {
  /** PF only, `YYYY-MM-DD`. */
  birthDate: string | null;
  origin: CustomerOrigin;
  address: Address;
  note: string | null;
  customFields: CustomFieldValues;
  contactCount: number;
}

export interface CustomerList {
  items: CustomerSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ContactPhone {
  /** Digits only, 10 or 11. */
  number: string;
  label: PhoneLabel;
}

export interface Contact {
  id: string;
  customerId: string;
  name: string;
  jobTitle: string | null;
  isPrimary: boolean;
  phones: ContactPhone[];
  emails: string[];
  /** The username, without `@`. */
  instagram: string | null;
  /**
   * PF/PJ, the company the person speaks for and its CPF/CNPJ (spec 071): a contact without a
   * customer's; a contact of a customer comes with `individual` and nulls.
   */
  type: CustomerType;
  company: string | null;
  document: string | null;
  customFields: CustomFieldValues;
  /**
   * The fields of a contact without a customer (spec 033): a contact of a customer is placed by
   * the customer, so they come empty here.
   */
  ownerMembershipId: string | null;
  unitId: string | null;
  originChannel: OriginChannel | null;
  originDetail: string | null;
  note: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A person found across customers, with the customer it belongs to (the start dialog's "Para"). */
export interface ContactSearchHit {
  id: string;
  name: string;
  jobTitle: string | null;
  isPrimary: boolean;
  phones: ContactPhone[];
  emails: string[];
  customer: { id: string; displayName: string; type: CustomerType; situation: string };
}

export const DUPLICATE_REASONS = ['document', 'phone', 'email'] as const;
export type DuplicateReason = (typeof DUPLICATE_REASONS)[number];

/**
 * A possible duplicate. The search covers the whole organization, but only a customer the
 * caller can open is named: one out of their reach says only why it matched — no name, no
 * owner, no id — so the check never leaks someone else's record.
 */
export type DuplicateMatch =
  | {
      visible: true;
      reasons: DuplicateReason[];
      customer: {
        id: string;
        displayName: string;
        type: CustomerType;
        archived: boolean;
        ownerName: string | null;
      };
    }
  | { visible: false; reasons: DuplicateReason[]; customer: null };

export interface DuplicatesResult {
  matches: DuplicateMatch[];
}

/* ------------------------------------------------------------------ schemas */

const personName = z.string().trim().min(CUSTOMER_LIMITS.nameMin).max(CUSTOMER_LIMITS.nameMax);
const optionalText = (max: number) => z.string().trim().max(max).nullable();

export const phoneNumberSchema = z.string().regex(/^\d{10,11}$/);
export const emailSchema = z.email().max(254);
export const instagramSchema = z.string().regex(/^[A-Za-z0-9._]{1,30}$/);

const birthDateSchema = z
  .string()
  .refine(isIsoDate, 'invalid_date')
  .refine((value) => value <= new Date().toISOString().slice(0, 10), 'future_date');

export const addressSchema = z
  .object({
    street: optionalText(160),
    number: optionalText(20),
    complement: optionalText(80),
    district: optionalText(80),
    city: optionalText(80),
    state: z.string().refine(isBrazilianState, 'invalid_state').nullable(),
    postalCode: z
      .string()
      .regex(/^\d{8}$/)
      .nullable(),
  })
  .strict();

export const originSchema = z
  .object({
    channel: z.enum(ORIGIN_CHANNELS).nullable(),
    detail: optionalText(CUSTOMER_LIMITS.originDetailMax),
  })
  .strict();

export const tagNamesSchema = z
  .array(z.string().trim().min(1).max(CUSTOMER_LIMITS.tagMax))
  .max(CUSTOMER_LIMITS.tagsMax)
  .refine((names) => new Set(names.map(normalizeLabel)).size === names.length, 'duplicated_tag');

/** Fields shared by creating and editing a customer. */
const customerFields = {
  name: personName,
  tradeName: optionalText(CUSTOMER_LIMITS.nameMax).optional(),
  document: z.string().regex(DOCUMENT_CHARACTERS).nullable().optional(),
  birthDate: birthDateSchema.nullable().optional(),
  ownerMembershipId: z.string().min(1).nullable().optional(),
  unitId: z.string().min(1).optional(),
  tags: tagNamesSchema.optional(),
  origin: originSchema.optional(),
  address: addressSchema.optional(),
  note: optionalText(CUSTOMER_LIMITS.noteMax).optional(),
  customFields: customFieldValuesSchema.optional(),
};

export const createCustomerSchema = z
  .object({
    type: z.enum(CUSTOMER_TYPES),
    ...customerFields,
    /** PF: the person themself; PJ: rule 20 of the spec. */
    primaryContact: z
      .object({
        name: personName.optional(),
        phone: phoneNumberSchema.optional(),
        email: emailSchema.optional(),
        /** A loose contact (spec 033) that becomes the primary contact instead of a new one. */
        contactId: z.string().min(1).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export type CreateCustomerInput = z.input<typeof createCustomerSchema>;

export const updateCustomerSchema = z
  .object({
    ...customerFields,
    name: personName.optional(),
    /** PF: renaming the customer renames its primary contact too. */
    syncPrimaryContactName: z.boolean().optional(),
    /**
     * Marks the customer inactive by hand (`true`) or reactivates it (`false`). The other
     * situations are computed from the deals (spec 005, rule 27).
     */
    inactive: z.boolean().optional(),
  })
  .strict();

export type UpdateCustomerInput = z.input<typeof updateCustomerSchema>;

export interface CustomerTypeIssue {
  field: 'tradeName' | 'birthDate' | 'document';
  code: 'NOT_ALLOWED_FOR_TYPE' | 'INVALID_DOCUMENT';
}

/**
 * The rules that depend on the type (spec rule 2): a CPF on a PF, a CNPJ on a PJ; the trade
 * name only on a PJ, the birth date only on a PF.
 */
export function customerTypeIssues(
  type: CustomerType,
  input: { tradeName?: string | null; birthDate?: string | null; document?: string | null },
): CustomerTypeIssue[] {
  const issues: CustomerTypeIssue[] = [];
  if (type === 'individual' && input.tradeName) {
    issues.push({ field: 'tradeName', code: 'NOT_ALLOWED_FOR_TYPE' });
  }
  if (type === 'company' && input.birthDate) {
    issues.push({ field: 'birthDate', code: 'NOT_ALLOWED_FOR_TYPE' });
  }
  if (input.document) {
    const valid = type === 'individual' ? isValidCpf(input.document) : isValidCnpj(input.document);
    if (!valid) issues.push({ field: 'document', code: 'INVALID_DOCUMENT' });
  }
  return issues;
}

const contactPhoneSchema = z
  .object({ number: phoneNumberSchema, label: z.enum(PHONE_LABELS) })
  .strict();

const contactFields = {
  name: personName,
  jobTitle: optionalText(CUSTOMER_LIMITS.jobTitleMax).optional(),
  phones: z.array(contactPhoneSchema).max(CUSTOMER_LIMITS.phonesMax).optional(),
  emails: z.array(emailSchema).max(CUSTOMER_LIMITS.emailsMax).optional(),
  instagram: instagramSchema.nullable().optional(),
  customFields: customFieldValuesSchema.optional(),
};

export const createContactSchema = z.object(contactFields).strict();
export type ContactInput = z.input<typeof createContactSchema>;

export const updateContactSchema = z
  .object({ ...contactFields, name: personName.optional() })
  .strict();
export type UpdateContactInput = z.input<typeof updateContactSchema>;

/** The query of `GET /customers`, as the API reads it (custom field filters apart). */
export const customerListQuerySchema = z.object({
  q: z.string().trim().max(CUSTOMER_LIMITS.searchMax).optional(),
  type: z.enum(CUSTOMER_TYPES).optional(),
  situation: z.enum(CUSTOMER_SITUATIONS).optional(),
  ownerMembershipId: z.string().min(1).optional(),
  unitId: z.string().min(1).optional(),
  tag: z.string().min(1).optional(),
  archived: z.enum(['true', 'false']).optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional(),
  sort: z.enum(CUSTOMER_SORTS).optional(),
});

export interface CustomerListQuery {
  q?: string;
  type?: CustomerType;
  situation?: CustomerSituation;
  /** A membership id or `none`. */
  ownerMembershipId?: string;
  unitId?: string;
  /** A tag id. */
  tag?: string;
  /** Only the archived ones. */
  archived?: boolean;
  page?: number;
  pageSize?: number;
  sort?: CustomerSort;
  /** `{ [key]: value }` → `cf.<key>=<value>`. */
  customFields?: Record<string, string>;
}

export const duplicatesQuerySchema = z.object({
  document: z.string().regex(DOCUMENT_CHARACTERS).optional(),
  phone: z.string().regex(/^\d+$/).optional(),
  email: z.string().trim().max(254).optional(),
  excludeId: z.string().min(1).optional(),
});

export interface DuplicatesQuery {
  document?: string;
  phone?: string;
  email?: string;
  excludeId?: string;
}

/* ------------------------------------------------------------------ helpers */

/** PF → name; PJ → trade name, or the legal name without one (spec rule 3). */
export function customerDisplayName(customer: {
  type: CustomerType;
  name: string;
  tradeName?: string | null;
}): string {
  return customer.type === 'company' && customer.tradeName ? customer.tradeName : customer.name;
}

/** The empty address (every part `null`). */
export function emptyAddress(): Address {
  return {
    street: null,
    number: null,
    complement: null,
    district: null,
    city: null,
    state: null,
    postalCode: null,
  };
}
